import { HubItemCategoryModel } from "../Hub/hub";
import { UserIdentityModel } from "../UserProfile/service";
import {
	HubItemMediaSaveModel,
	HubItemMediaComposerViewModel,
	HubItemLayoutSaveModel,
	HubItemLayoutComposerViewModel,
	HubLinkedDocumentSaveModel,
	HubHierarchyListNodeModel
} from "../Hub/common.composer";
import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import { environmentHttpClient } from "../httpClient";
import { TagSaveModel, TagViewModel } from "client/Api/Tags/tags";
import { HubItemImageRestrictionsModel, HubLinkedDocumentViewModel } from "../Hub/common";
import { DocumentLocationViewModel } from "../Hub/hubSettings";

export enum AnnouncementStatus {
	Draft = 1,
	Published = 2,
	Planned = 3,
	Archived = 4
}

export type AnnouncementListItemModel = {
	id: number;
	title: string;
	status: AnnouncementStatus;
	startTime: Date | string | null;
	endTime: Date | string | null;
	createdByUserName: string;
	modifiedByUserName: string;
	modifiedDate: Date | string;
	viewsCount: number;
	reactionsCount: number;
	commentsCount: number;
	categoryIds: readonly number[];
	isPinned: boolean;
	tags: readonly TagViewModel[];
	canBeEditedByUser: boolean;
};

export type AnnouncementListModel = {
	announcements: AnnouncementListItemModel[];
	categories: HubItemCategoryModel[];
	canCreateTemplates: boolean;
};

export type AnnouncementSaveModel = {
	title: string;
	layout: HubItemLayoutSaveModel;
	media: HubItemMediaSaveModel;
	showAutomatically: boolean;
	startTime: Date | null;
	endTime: Date | null;
	isForEveryoneInTheCategories: boolean;
	recipients: HubItemAccessModel | null;
	shouldShowAuthor: boolean;
	areCommentsEnabled: boolean;
	categoryIds: readonly number[];
	isPinned: boolean;
	tags: TagSaveModel[];
	linkedDocuments: readonly HubLinkedDocumentSaveModel[];
};

export type AnnouncementSaveRequestModel = {
	announcementSaveModel: AnnouncementSaveModel | PublishedAnnouncementSaveModel;
	shouldPublishAnnouncement: boolean;
};

export type HubItemAccessModel = {
	userIds: number[];
	userGroupIds: number[];
};

export type AnnouncementViewModel = {
	id?: number;
	title: string | null;
	layout: HubItemLayoutComposerViewModel | null;
	media: HubItemMediaComposerViewModel;
	showAutomatically: boolean;
	startTime: string | null;
	endTime: string | null;
	isForEveryoneInTheCategories: boolean;
	recipients: HubItemAccessModel;
	shouldShowAuthor: boolean;
	areCommentsEnabled: boolean;
	categoryIds: readonly number[];
	isPinned: boolean;
	tags: readonly TagViewModel[];
	linkedDocuments: readonly HubLinkedDocumentViewModel[] | null;
};

export type EditAnnouncementPageViewModel = {
	announcement: AnnouncementViewModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	announcementHierarchy: readonly HubHierarchyListNodeModel[];
	canCreateNewCategory: boolean;
	canSendDirectPopups: boolean;
	imageRestrictions: HubItemImageRestrictionsModel;
	defaultImageLocation: DocumentLocationViewModel | null;
	status: AnnouncementStatus;
	createdBy: UserIdentityModel | null;
	isPixabayImageGalleryAvailable: boolean;
};

export type AnnouncementSaveResult = {
	announcementId: number;
};

export type PublishedAnnouncementSaveModel = {
	title: string;
	layout: HubItemLayoutSaveModel;
	media: HubItemMediaSaveModel;
	shouldShowAuthor: boolean;
	areCommentsEnabled: boolean;
	categoryIds: readonly number[];
	endTime: Date | null;
	isPinned: boolean;
	tags: TagSaveModel[];
	linkedDocuments: readonly HubLinkedDocumentSaveModel[];
};

export type AnnouncementPinModel = {
	id: number;
	title: string;
	status: AnnouncementStatus;
};

export type CheckAnnouncementPinLimitResult = {
	canBePinned: boolean;
	pinnedItems: readonly AnnouncementPinModel[];
};

export function listAnnouncements(signal?: AbortSignal): Promise<AnnouncementListModel> {
	return environmentHttpClient.get<AnnouncementListModel>(
		"UiApi/Announcements/List",
		undefined,
		{ signal }
	);
}

export function getNewModel(
	announcementToCopyId: undefined,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditAnnouncementPageViewModel>;
export function getNewModel(
	announcementToCopyId: number,
	templateId: undefined,
	signal?: AbortSignal
): Promise<EditAnnouncementPageViewModel>;
export function getNewModel(
	announcementToCopyId: undefined,
	templateId: number,
	signal?: AbortSignal
): Promise<EditAnnouncementPageViewModel>;
export function getNewModel(
	announcementToCopyId: number | undefined,
	templateId: number | undefined,
	signal?: AbortSignal
): Promise<EditAnnouncementPageViewModel> {
	return environmentHttpClient.get<EditAnnouncementPageViewModel>(
		"UiApi/Announcements/Add",
		{ announcementToCopyId, templateId },
		{ signal }
	);
}

export function create(
	announcement: AnnouncementSaveRequestModel,
	signal?: AbortSignal
): Promise<AnnouncementSaveResult> {
	return environmentHttpClient.post<AnnouncementSaveResult>(
		"UiApi/Announcements",
		announcement,
		{ signal }
	);
}

export function getEditModel(id: number, signal?: AbortSignal): Promise<EditAnnouncementPageViewModel> {
	return environmentHttpClient.get<EditAnnouncementPageViewModel>(
		`UiApi/Announcements/Edit/${id}`,
		undefined,
		{ signal }
	);
}

export function update(
	id: number,
	announcement: AnnouncementSaveRequestModel,
	signal?: AbortSignal
): Promise<AnnouncementSaveResult> {
	return environmentHttpClient.put<AnnouncementSaveResult>(
		`UiApi/Announcements/${id}`,
		announcement,
		{ signal }
	);
}

export function updatePublished(
	id: number,
	model: PublishedAnnouncementSaveModel,
	signal?: AbortSignal
): Promise<AnnouncementSaveResult> {
	return environmentHttpClient.put<AnnouncementSaveResult>(
		`UiApi/Announcements/Published/${id}`,
		model,
		{ signal }
	);
}

export function deleteAnnouncements(ids: number[], signal?: AbortSignal): Promise<void> {
	return environmentHttpClient.delete<void>("UiApi/Announcements", ids, { signal });
}

export function checkPinLimit(
	categoryId: number,
	announcementId: number | null,
	signal?: AbortSignal
): Promise<CheckAnnouncementPinLimitResult> {
	return environmentHttpClient.get<CheckAnnouncementPinLimitResult>(
		`UiApi/Announcements/CheckPinLimit`,
		{ categoryId, announcementId },
		{ signal }
	);
}

export function unPin(
	ids: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/Announcements/unPin",
		ids,
		{ signal }
	);
}

export function archiveAnnouncements(
	ids: number[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		"UiApi/Announcements/Archive",
		ids,
		{ signal }
	);
}
